import React, { useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import { formatDateTime } from "../../utils/formatters";
import packageJson from "../../../package.json";
import { tokens } from "../../theme";

interface UserMenuProps {
  email?: string;
  groups: string[];
  lastUpdate?: number;
  onSignOut: () => void;
  /** DEMO users can change the bots they follow. */
  onSelectBots?: () => void;
  /** `avatar` for top bars, `card` for the sidebar footer. */
  variant?: "avatar" | "card";
}

const UserAvatar: React.FC<{ initial: string; size?: number }> = ({ initial, size = 34 }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: "0.8125rem",
      bgcolor: alpha(tokens.gold, 0.16),
      color: tokens.goldLight,
      border: `1px solid ${alpha(tokens.gold, 0.45)}`,
    }}
  >
    {initial}
  </Avatar>
);

/** Account menu: identity, data freshness, version and sign out. */
export const UserMenu: React.FC<UserMenuProps> = ({
  email,
  groups,
  lastUpdate,
  onSignOut,
  onSelectBots,
  variant = "avatar",
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const initial = (email || groups[0] || "U").charAt(0).toUpperCase();
  const role = groups.includes("admin") ? "Admin" : groups.includes("DEMO") ? "Demo" : groups.join(" · ");

  return (
    <>
      {variant === "card" ? (
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Account menu"
          sx={{
            width: "100%",
            justifyContent: "flex-start",
            gap: 1.25,
            p: 1,
            borderRadius: "16px",
            textAlign: "left",
            "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
          }}
        >
          <UserAvatar initial={initial} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {/* Local part only: the sidebar is narrow; the menu shows the full address. */}
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 650 }} noWrap title={email}>
              {email ? email.split("@")[0] : role || "Signed in"}
            </Typography>
            {/* Data freshness lives in the page header; the account card says who you are. */}
            {email && role && (
              <Typography sx={{ mt: 0.25, fontSize: "0.75rem", color: "text.secondary" }} noWrap>
                {role}
              </Typography>
            )}
          </Box>
          <UnfoldMoreRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        </ButtonBase>
      ) : (
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Account menu"
          sx={{ p: 0.25, borderRadius: "50%" }}
        >
          <UserAvatar initial={initial} size={36} />
        </IconButton>
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={variant === "card" ? { vertical: "top", horizontal: "left" } : { vertical: "bottom", horizontal: "right" }}
        transformOrigin={variant === "card" ? { vertical: "bottom", horizontal: "left" } : { vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: variant === "card" ? -1 : 1, minWidth: 248 } } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 1.25 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 650 }} noWrap>
            {email || "Signed in"}
          </Typography>
          {role && (
            <Typography variant="caption" color="text.secondary">
              {role}
            </Typography>
          )}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {onSelectBots && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              onSelectBots();
            }}
          >
            <ListItemIcon>
              <TuneRoundedIcon fontSize="small" />
            </ListItemIcon>
            Choose strategies
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onSignOut();
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, py: 0.75 }}>
          <Typography variant="caption" color="text.secondary">
            v{packageJson.version}
          </Typography>
          {lastUpdate && (
            <Typography variant="caption" color="text.secondary">
              Data {formatDateTime(lastUpdate)}
            </Typography>
          )}
        </Stack>
      </Menu>
    </>
  );
};

export default UserMenu;
